/** Types shared by the HTTP API and the browser client. */

export const BOOK_STATUSES = ['pending', 'complete', 'not_found', 'error'] as const;

/**
 * - pending:   saved, waiting for the background worker to fetch metadata
 * - complete:  metadata fetched from a catalogue (see `source`)
 * - not_found: no catalogue has a record for the ISBN
 * - error:     metadata fetch failed after all retries (can be refreshed)
 */
export type BookStatus = (typeof BOOK_STATUSES)[number];

/** Catalogues that metadata can come from, in the order they are tried. */
export const METADATA_SOURCES = ['open_library', 'google_books'] as const;
export type MetadataSourceId = (typeof METADATA_SOURCES)[number];

/** Rough height of a physical copy, recorded by its owner. */
export const BOOK_SIZES = ['under_200mm', 'under_250mm', 'large_print'] as const;
export type BookSize = (typeof BOOK_SIZES)[number];

export const BOOK_SIZE_LABELS: Record<BookSize, string> = {
  under_200mm: '<200mm',
  under_250mm: '<250mm',
  large_print: 'Large print',
};

/** Book metadata normalised from a catalogue record. */
export interface BookMetadata {
  title: string;
  subtitle: string | null;
  authors: string[];
  publishers: string[];
  publishDate: string | null;
  numberOfPages: number | null;
  subjects: string[];
  coverUrl: string | null;
  coverLargeUrl: string | null;
  /** Which catalogue the metadata came from. */
  source: MetadataSourceId;
  /** The book's page on that catalogue. */
  sourceUrl: string | null;
}

/**
 * A book in the signed-in user's collection. Metadata belongs to the ISBN and
 * is shared between users; `addedAt` is when this user added it.
 */
export interface Book extends Omit<BookMetadata, 'title' | 'source'> {
  isbn: string;
  status: BookStatus;
  /** Null until metadata has been fetched, or when no catalogue has a record. */
  title: string | null;
  source: MetadataSourceId | null;
  lastError: string | null;
  /** This user's categories for the book (built-in ones first). */
  categories: CategoryRef[];
  /** Height of this user's copy; null until they set it. */
  size: BookSize | null;
  addedAt: string;
  /** When the book's metadata last changed. */
  updatedAt: string;
}

export interface CategoryRef {
  id: number;
  name: string;
}

export interface Category extends CategoryRef {
  /** Built-in categories are offered to everyone and can't be renamed or deleted. */
  builtIn: boolean;
  /** How many books in the signed-in user's collection are in it. */
  bookCount: number;
}

export const MAX_CATEGORY_NAME_LENGTH = 40;

export interface CategoryListResponse {
  categories: Category[];
}

export interface CategoryResponse {
  category: Category;
}

export interface CategoryRequest {
  name: string;
}

export interface SetSizeRequest {
  size: BookSize | null;
}

export interface SetCategoriesRequest {
  categoryIds: number[];
}

export interface User {
  id: number;
  email: string;
  name: string | null;
  pictureUrl: string | null;
}

export interface MeResponse {
  user: User;
}

/** Someone the signed-in user has agreed to share collections with. */
export interface Friend extends User {
  bookCount: number;
  /** When the friendship started. */
  since: string;
}

/** A request someone sent to the signed-in user's email address. */
export interface IncomingFriendRequest {
  id: number;
  from: User;
  createdAt: string;
}

/**
 * A request the signed-in user sent. Only the email is shown, so requests
 * can't be used to find out who has an account.
 */
export interface OutgoingFriendRequest {
  id: number;
  email: string;
  createdAt: string;
}

export interface FriendsResponse {
  friends: Friend[];
  incoming: IncomingFriendRequest[];
  outgoing: OutgoingFriendRequest[];
}

export interface FriendRequestRequest {
  email: string;
}

/**
 * - sent:            a new request is waiting for them to accept
 * - already_sent:    you had already asked them
 * - accepted:        they had already asked you, so you're now friends
 * - already_friends: nothing to do
 */
export type FriendRequestOutcome = 'sent' | 'already_sent' | 'accepted' | 'already_friends';

export interface FriendRequestResponse {
  outcome: FriendRequestOutcome;
}

export interface FriendResponse {
  friend: Friend;
}

/** A friend's collection. Books only list built-in categories; a user's own categories stay private. */
export interface FriendCollectionResponse {
  friend: Friend;
  books: Book[];
}

// --- Households ---

/** Someone sharing one collection with the signed-in user (the user themselves included). */
export interface HouseholdMember extends User {
  joinedAt: string;
}

/** The household (empty members if not in one), invitations waiting for the user, and ones they've sent. */
export interface HouseholdResponse {
  members: HouseholdMember[];
  incoming: IncomingFriendRequest[];
  outgoing: OutgoingFriendRequest[];
}

export interface HouseholdInviteRequest {
  email: string;
}

/** sent (201); already_sent; already_member (they're in your household already). */
export type HouseholdInviteOutcome = 'sent' | 'already_sent' | 'already_member';

export interface HouseholdInviteResponse {
  outcome: HouseholdInviteOutcome;
}

/** Values of the `signin_error` query parameter the server redirects to after a failed sign-in. */
export type SignInError = 'not_configured' | 'cancelled' | 'expired' | 'not_allowed' | 'unverified_email' | 'failed';

export interface LookupResponse {
  isbn: string;
  /** Null when no catalogue has a record for the ISBN. */
  metadata: BookMetadata | null;
  /** Display names of the catalogues that were searched, e.g. ["Open Library", "Google Books"]. */
  sourcesChecked: string[];
  /** The saved book, if this ISBN is already in the collection. */
  existing: Book | null;
}

export interface AddBookRequest {
  isbn: string;
  /** Also put the book in this category (it's added even if the book was already in the collection). */
  categoryId?: number | null;
  /** Also set the copy's size (also for a book already in the collection). Null leaves it unchanged. */
  size?: BookSize | null;
}

export interface AddBookResponse {
  book: Book;
  /** False when the ISBN was already in the collection. */
  created: boolean;
  /** True when the requested category was newly applied to the book. */
  categoryAdded: boolean;
  /** True when the requested size was different from the one saved before. */
  sizeChanged: boolean;
}

export interface BookListResponse {
  books: Book[];
}

export interface BookResponse {
  book: Book;
}

export interface ExportDocument {
  exportedAt: string;
  /** Email of the user whose collection this is. */
  owner: string;
  count: number;
  books: Book[];
  bottles: Bottle[];
}

// --- Drinks ---

export const DRINK_KINDS = ['wine', 'beer', 'cider', 'spirit', 'premix', 'other'] as const;
export type DrinkKind = (typeof DRINK_KINDS)[number];

export const DRINK_KIND_LABELS: Record<DrinkKind, string> = {
  wine: 'Wine',
  beer: 'Beer',
  cider: 'Cider',
  spirit: 'Spirit',
  premix: 'Pre-mix',
  other: 'Other',
};

export const MAX_BOTTLE_TEXT_LENGTH = 120;
export const MAX_BOTTLE_QUANTITY = 999;

/** What a barcode lookup tells us about a drink. */
export interface DrinkMetadata {
  name: string;
  brand: string | null;
  kind: DrinkKind;
  sizeMl: number | null;
  /** Alcohol by volume, percent. */
  abv: number | null;
  imageUrl: string | null;
  /** The product's page on the catalogue it came from. */
  sourceUrl: string | null;
}

/**
 * Everything the owner can edit about a bottle. Lookup results are copied onto
 * the bottle when it's saved, so bottles without a barcode work the same way.
 */
export interface BottleDetails {
  barcode: string | null;
  name: string;
  brand: string | null;
  kind: DrinkKind;
  /** Vintage, batch or season: barcodes are reused across these. */
  variant: string | null;
  sizeMl: number | null;
  abv: number | null;
  imageUrl: string | null;
  /** How much one "pour" takes off the open unit; null means a pour is a whole unit (a can, a stubby). */
  serveMl: number | null;
  /** Units on hand, including the open one. 0 means finished. */
  quantity: number;
  /** On the shopping list by hand. Cleared when restocked (the count goes up). */
  wantMore: boolean;
  /**
   * Keep stocked: on the shopping list whenever units left (unopened ones plus the open one's
   * level) drop to this or below, e.g. 0.25 for a quarter bottle, 2 for two cans. Null = off.
   */
  restockAt: number | null;
}

export interface Bottle extends BottleDetails {
  id: number;
  /** The owner's own photo, served by the app; shown in preference to `imageUrl` (the catalogue picture). */
  photoUrl: string | null;
  /** How much of the open unit is left, 0 to 1 (estimated from pours since it was last checked). */
  level: number;
  poursSinceCheck: number;
  /** When the owner last set the level by hand (initially when the bottle was added). */
  levelCheckedAt: string;
  addedAt: string;
  updatedAt: string;
}

export interface DrinkLookupResponse {
  barcode: string;
  /** Null when nothing is known about the barcode. */
  metadata: DrinkMetadata | null;
  /** Where `metadata` came from: a bottle someone has already saved, or the catalogue. */
  source: 'bar' | 'open_food_facts' | null;
  /** Bottles with this barcode already in the bar. */
  existing: Bottle[];
}

export type BottleRequest = BottleDetails;

export interface PourRequest {
  count: number;
}

/** A real check of a bottle: its actual level, and optionally how many units are on hand. */
export interface SetLevelRequest {
  level: number;
  quantity?: number;
}

export interface BottleListResponse {
  bottles: Bottle[];
}

export interface BottleResponse {
  bottle: Bottle;
}

export interface FriendBarResponse {
  friend: Friend;
  bottles: Bottle[];
}

export type ApiErrorCode =
  | 'UNAUTHENTICATED'
  | 'FORBIDDEN'
  | 'CONFLICT'
  | 'INVALID_ISBN'
  | 'INVALID_BARCODE'
  | 'INVALID_REQUEST'
  | 'NOT_FOUND'
  | 'UPSTREAM_TIMEOUT'
  | 'UPSTREAM_ERROR'
  | 'INTERNAL_ERROR';

export interface ApiErrorBody {
  error: {
    code: ApiErrorCode;
    message: string;
  };
}
